import { useCallback, useMemo, useState } from "react"
import type { TFurnitureCustomization } from "types/elevationReferences.type"

/** Backend cap on the note (REQ-413's justification text). */
export const CUSTOMIZATION_NOTE_MAX_LENGTH = 255

export type TCustomizationFields = {
	isExpanded: boolean
	note: string
	priceAdjustment: number | null
	setNote: (note: string) => void
	setPriceAdjustment: (value: number | null) => void
	/** The `+` half of the header button. */
	expand: () => void
	/** The local half of the `−`: fold the section away and forget both fields. */
	collapse: () => void
	/** Replace all three fields with a value that changed on the server (a peer window's edit). */
	resync: (next: TFurnitureCustomization | null) => void
	/**
	 * What the object's customization should be, or `null` when it should not have one at all.
	 *
	 * Non-null for as long as the section is OPEN, whatever the fields hold — an empty note and a
	 * null amount are a valid customization, and the section being open IS the user asking for
	 * one. Neither field gates it.
	 */
	payload: TFurnitureCustomization | null
}

/**
 * Field state of the shared Customization section (REQ-413/414), used by the furniture edit
 * sidebar and by all three create panels.
 *
 * The seed is read ONCE, on mount. A later server-side change (the pop-out's peer window) comes
 * in through `resync`, which the edit sidebar calls only when the value differs from what it last
 * saved itself — so its own commit echo can never clobber what the user is typing. A create panel
 * passes nothing and therefore starts collapsed and empty.
 *
 * `payload` tracks whether the object should HAVE a customization, which is simply whether the
 * section is open: opening it with `+` is the request, and both fields are optional from that
 * point on — the endpoint accepts an empty note and a null amount, and a customization with
 * neither is a legitimate placeholder the user fills in afterwards. Nothing is sent only while
 * the section is closed.
 */
export const useCustomizationFields = (initial?: TFurnitureCustomization | null): TCustomizationFields => {
	const [isExpanded, setIsExpanded] = useState(initial != null)
	const [note, setNote] = useState(initial?.note ?? "")
	const [priceAdjustment, setPriceAdjustment] = useState<number | null>(initial?.price_adjustment ?? null)

	const expand = useCallback(() => setIsExpanded(true), [])

	const collapse = useCallback(() => {
		setIsExpanded(false)
		setNote("")
		setPriceAdjustment(null)
	}, [])

	const resync = useCallback((next: TFurnitureCustomization | null) => {
		setIsExpanded(next != null)
		setNote(next?.note ?? "")
		setPriceAdjustment(next?.price_adjustment ?? null)
	}, [])

	const payload = useMemo<TFurnitureCustomization | null>(() => {
		if (!isExpanded) return null
		// The note goes to the wire exactly as typed — never trimmed. REQ-414 requires the text
		// structure to survive close/open/reload and names REQ-247's comment field as the
		// reference; trimming would silently eat leading indentation and trailing blank lines on
		// every save.
		return { note, price_adjustment: priceAdjustment }
	}, [isExpanded, note, priceAdjustment])

	return { isExpanded, note, priceAdjustment, setNote, setPriceAdjustment, expand, collapse, resync, payload }
}
